import * as THREE from 'three';
import type { VisibleModelEdgeProjectionRecord } from '../../../types/sketch/VisibleModelEdgeProjection';
import { markCadObjectAsRuntimeDerived } from '../../document';
import { LineMaterialFactory, LineMaterialState } from '../../material';
import { addObject3DToParent } from '../../utils';

/**
 * 当前捕捉源模型边的瞬态高亮。
 *
 * 只消费投影服务已经裁剪的源边段，不重新解析拓扑或创建选择代理。独立持有几何和材质，
 * 普通 hover、选中刷新及代理回收均不会覆盖本对象；销毁也不会改变源模型的显示状态。
 */
export class ModelEdgeSnapHighlight extends THREE.LineSegments<THREE.BufferGeometry, THREE.LineBasicMaterial> {
    /** 辅助视觉对象不得被当作正式模型或草图实体。 */
    public readonly isAuxiliaryObject = true;

    /** 保证会话清理与 Viewer 释放重复调用时只释放一次资源。 */
    private disposed = false;

    /**
     * 创建源边高亮并挂到所属结果体，使高亮自动跟随结果体变换和可见性。
     *
     * @param record 当前唤醒的模型边及其可见源边段。
     */
    public constructor(record: VisibleModelEdgeProjectionRecord) {
        const geometry = new THREE.BufferGeometry();
        const material = LineMaterialFactory.getFeatureLineMaterialByState(LineMaterialState.Hover).clone();
        super(geometry, material);

        // 源边与模型表面共面；只画已通过遮挡裁剪的边段，并避免深度竞争吞掉高亮。
        material.depthTest = false;
        material.depthWrite = false;
        this.name = '模型边捕捉高亮';
        this.renderOrder = 7;
        this.frustumCulled = false;
        // 通过文档层正式约定排除持久化，避免捕捉期间保存项目时写入临时高亮。
        markCadObjectAsRuntimeDerived(this);

        // 投影记录使用世界坐标，挂载到模型下前统一反变换，避免组件位姿被重复应用。
        record.sourceMesh.updateWorldMatrix(true, false);
        const worldToLocal = record.sourceMesh.matrixWorld.clone().invert();
        const localPoint = new THREE.Vector3();
        const positions = new Float32Array(record.visibleSegments.length * 6);
        record.visibleSegments.forEach((segment, index) => {
            localPoint.copy(segment.sourceStart).applyMatrix4(worldToLocal).toArray(positions, index * 6);
            localPoint.copy(segment.sourceEnd).applyMatrix4(worldToLocal).toArray(positions, index * 6 + 3);
        });
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        addObject3DToParent(this, record.sourceMesh);
    }

    /**
     * 高亮只提供反馈，不参与射线拾取，防止自身遮挡真实模型边或再次触发捕捉。
     */
    public override raycast(): void {
        // 保留空实现；统一 BVH 入场链路会尊重对象显式禁用的 raycast。
    }

    /**
     * 移除高亮并释放独占资源；共享模型几何和工厂材质不受影响。
     */
    public dispose(): void {
        if (this.disposed) {
            return;
        }
        this.disposed = true;
        this.removeFromParent();
        this.geometry.dispose();
        this.material.dispose();
    }
}
